import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import agent from '../../agent';
import CatchesMapComponent from './CatchesMapComponent';
import CatchLightbox from './CatchLightbox';

const SectionHeader = ({ icon, label, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <Box sx={{ color: DEFAULT_THEME_COLOR, display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: DEFAULT_THEME_COLOR, flex: 1 }}
    >
      {label}
    </Typography>
    <Box sx={{
      bgcolor: 'rgba(54,201,162,0.15)',
      borderRadius: 10,
      px: 1,
      py: 0.25,
      minWidth: 26,
      textAlign: 'center',
    }}>
      <Typography variant="caption" sx={{ color: DEFAULT_THEME_COLOR, fontWeight: 700, fontSize: 12 }}>
        {count}
      </Typography>
    </Box>
  </Box>
);

const EmptyState = ({ label }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 2,
    bgcolor: 'rgba(0,0,0,0.02)',
    borderRadius: 2,
    mb: 0.5,
  }}>
    <SentimentDissatisfiedIcon sx={{ color: 'rgba(0,0,0,0.2)', mb: 0.5 }} />
    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
      {label}
    </Typography>
  </Box>
);

const CatchItem = ({ item, onEdit, onDelete, onImageClick }) => (
  <Box sx={{
    mb: 1.5,
    p: 1.5,
    borderRadius: 2,
    bgcolor: 'rgba(54,201,162,0.07)',
    border: '1px solid rgba(54,201,162,0.18)',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
          {item.species}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(item.caughtAt).toLocaleDateString('pl-PL')}
          {item.weightKg != null ? ` · ${item.weightKg} kg` : ''}
          {item.lengthCm != null ? ` · ${item.lengthCm} cm` : ''}
        </Typography>
        {item.notes && (
          <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', mt: 0.5, opacity: 0.7 }}>
            {item.notes}
          </Typography>
        )}
        {item.imageUrl && (
          <Box
            component="img"
            src={item.imageUrl}
            alt="połów"
            onClick={() => onImageClick?.(item)}
            sx={{
              width: '100%',
              height: 160,
              objectFit: 'cover',
              borderRadius: 1.5,
              mt: 1,
              cursor: 'pointer',
              transition: 'opacity 0.15s',
              '&:hover': { opacity: 0.88 },
            }}
          />
        )}
      </Box>
      {onEdit && onDelete && (
        <Box sx={{ display: 'flex', ml: 0.5, mt: -0.5, flexShrink: 0 }}>
          <IconButton size="small" onClick={onEdit} sx={{ color: DEFAULT_THEME_COLOR }}>
            <EditIcon fontSize="inherit" />
          </IconButton>
          <IconButton size="small" onClick={onDelete} sx={{ color: '#ff5252' }}>
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </Box>
      )}
    </Box>
  </Box>
);

const CatchLogSection = ({ catches, isLoading, onRefresh, placeLat, placeLon }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const fileInputRef = useRef(null);
  const [knownSpecies, setKnownSpecies] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    agent.FishSpecies.getAll().then(setKnownSpecies).catch(() => {});
  }, []);

  const [editing, setEditing] = useState(null);
  const [editSpecies, setEditSpecies] = useState('');
  const [editCaughtAt, setEditCaughtAt] = useState(dayjs());
  const [editWeightKg, setEditWeightKg] = useState('');
  const [editLengthCm, setEditLengthCm] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editCurrentImageUrl, setEditCurrentImageUrl] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editImagePreviewError, setEditImagePreviewError] = useState(false);
  const [editImageRemoved, setEditImageRemoved] = useState(false);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  function openEdit(item) {
    setEditing(item);
    setEditSpecies(item.species);
    setEditCaughtAt(dayjs(item.caughtAt));
    setEditWeightKg(item.weightKg != null ? String(item.weightKg) : '');
    setEditLengthCm(item.lengthCm != null ? String(item.lengthCm) : '');
    setEditNotes(item.notes ?? '');
    setEditCurrentImageUrl(item.imageUrl ?? null);
    setEditImageFile(null);
    if (editImagePreview) URL.revokeObjectURL(editImagePreview);
    setEditImagePreview(null);
    setEditImagePreviewError(false);
    setEditImageRemoved(false);
    setEditError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleEditImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (editImagePreview) URL.revokeObjectURL(editImagePreview);
    setEditImageFile(file);
    setEditImagePreview(URL.createObjectURL(file));
    setEditImagePreviewError(false);
    setEditImageRemoved(false);
  }

  function removeEditImage() {
    if (editImagePreview) URL.revokeObjectURL(editImagePreview);
    setEditImageFile(null);
    setEditImagePreview(null);
    setEditImagePreviewError(false);
    setEditCurrentImageUrl(null);
    setEditImageRemoved(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const displayedImage = (editImagePreview && !editImagePreviewError) ? editImagePreview : editCurrentImageUrl;

  async function handleEditSubmit() {
    if (!editSpecies.trim()) {
      setEditError('Podaj gatunek ryby');
      return;
    }
    setSaving(true);
    try {
      let imageUrl = undefined;

      if (editImageFile) {
        const result = await agent.CatchLog.uploadImage(editImageFile);
        imageUrl = result.imageUrl;
      } else if (editImageRemoved) {
        imageUrl = null;
      }

      await agent.CatchLog.update(editing.id, {
        species: editSpecies.trim().toLowerCase(),
        caughtAt: editCaughtAt.toISOString(),
        weightKg: editWeightKg ? parseFloat(editWeightKg) : null,
        lengthCm: editLengthCm ? parseFloat(editLengthCm) : null,
        notes: editNotes.trim() || null,
        ...(imageUrl !== undefined ? { imageUrl } : {}),
      });
      setEditing(null);
      onRefresh?.();
    } catch {
      setEditError('Błąd zapisu. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleteLoading(true);
    try {
      await agent.CatchLog.delete(deleting.id);
      setDeleting(null);
      onRefresh?.();
    } catch {
      setDeleteError('Błąd usuwania. Spróbuj ponownie.');
    } finally {
      setDeleteLoading(false);
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} sx={{ color: DEFAULT_THEME_COLOR }} />
      </Box>
    );
  }

  const mine = (catches || []).filter(c => c.isMine);
  const others = (catches || []).filter(c => !c.isMine);

  // Viewer navigates through the catches in the same order they are listed (mine, then others).
  const viewerItems = [...mine, ...others];
  const openLightbox = (item) => {
    const idx = viewerItems.findIndex(c => c.id === item.id);
    if (idx !== -1) setLightboxIndex(idx);
  };

  return (
    <Box sx={{ maxWidth: isDesktop ? 780 : '100%', mx: 'auto' }}>

      <CatchesMapComponent catches={catches} placeLat={placeLat} placeLon={placeLon} />

      <SectionHeader icon={<PersonIcon fontSize="small" />} label="Moje połowy" count={mine.length} />

      {mine.length === 0 ? (
        <EmptyState label="Nie masz jeszcze połowów w tym miejscu" />
      ) : isDesktop ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
          {mine.map(c => (
            <CatchItem
              key={c.id}
              item={c}
              onEdit={() => openEdit(c)}
              onDelete={() => { setDeleting(c); setDeleteError(''); }}
              onImageClick={openLightbox}
            />
          ))}
        </Box>
      ) : (
        mine.map(c => (
          <CatchItem
            key={c.id}
            item={c}
            onEdit={() => openEdit(c)}
            onDelete={() => { setDeleting(c); setDeleteError(''); }}
            onImageClick={openLightbox}
          />
        ))
      )}

      <Divider sx={{ my: 2 }} />

      <SectionHeader icon={<GroupIcon fontSize="small" />} label="Połowy innych" count={others.length} />

      {others.length === 0 ? (
        <EmptyState label="Brak połowów od innych użytkowników" />
      ) : isDesktop ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {others.map(c => (
            <Box key={c.id}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                @{c.username}
              </Typography>
              <CatchItem item={c} onImageClick={openLightbox} />
            </Box>
          ))}
        </Box>
      ) : (
        others.map(c => (
          <Box key={c.id}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              @{c.username}
            </Typography>
            <CatchItem item={c} onImageClick={openLightbox} />
          </Box>
        ))
      )}

      {/* Fullscreen image lightbox */}
      <CatchLightbox
        items={viewerItems}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />

      {/* Edit dialog */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>Edytuj połów</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editError && <Alert severity="error">{editError}</Alert>}
            <Autocomplete
              freeSolo
              options={[...knownSpecies, 'inny']}
              inputValue={editSpecies}
              onInputChange={(_, val) => setEditSpecies(val)}
              onChange={(_, val) => { if (val === 'inny') setEditSpecies(''); }}
              getOptionLabel={opt => opt === 'inny' ? 'inny (wpisz nazwę)' : opt}
              renderInput={params => <TextField {...params} label="Gatunek" size="small" />}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDateTimePicker
                label="Data i godzina połowu"
                value={editCaughtAt}
                onChange={val => val && setEditCaughtAt(val)}
                maxDateTime={dayjs()}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Waga (kg)"
                value={editWeightKg}
                onChange={e => setEditWeightKg(e.target.value)}
                size="small"
                type="number"
                inputProps={{ step: '0.1', min: '0' }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Długość (cm)"
                value={editLengthCm}
                onChange={e => setEditLengthCm(e.target.value)}
                size="small"
                type="number"
                inputProps={{ step: '0.1', min: '0' }}
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Notatki"
              value={editNotes}
              onChange={e => setEditNotes(e.target.value)}
              size="small"
              multiline
              rows={2}
            />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleEditImageChange}
            />
            {(editImageFile || editCurrentImageUrl) ? (
              <Box sx={{ position: 'relative' }}>
                {displayedImage ? (
                  <Box
                    component="img"
                    src={displayedImage}
                    alt="zdjęcie połowu"
                    onError={() => setEditImagePreviewError(true)}
                    sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 1, display: 'block' }}
                  />
                ) : (
                  <Box sx={{ p: 1.5, border: `1px solid ${DEFAULT_THEME_COLOR}`, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddPhotoAlternateIcon sx={{ color: DEFAULT_THEME_COLOR }} />
                    <Typography variant="caption" noWrap sx={{ flex: 1 }}>{editImageFile?.name}</Typography>
                  </Box>
                )}
                <IconButton
                  size="small"
                  onClick={removeEditImage}
                  sx={{
                    position: 'absolute', top: 4, right: 4,
                    bgcolor: 'rgba(0,0,0,0.5)', color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  borderColor: DEFAULT_THEME_COLOR,
                  color: DEFAULT_THEME_COLOR,
                  '&:hover': { borderColor: DEFAULT_THEME_COLOR, bgcolor: 'rgba(54,201,162,0.06)' },
                }}
              >
                Dodaj zdjęcie
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Anuluj</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={saving}
            sx={{ bgcolor: DEFAULT_THEME_COLOR, '&:hover': { bgcolor: '#2aad8a' } }}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs">
        <DialogTitle>Usuń połów</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 1 }}>{deleteError}</Alert>}
          <Typography>
            Czy na pewno chcesz usunąć wpis „{deleting?.species}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Anuluj</Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
            sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#d32f2f' } }}
          >
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CatchLogSection;
